/*********************************************************
 * Third Components
*********************************************************/
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react'
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect'

/*********************************************************
 * Components
*********************************************************/
import { Select, Input } from 'Components';
import InputSearchMap from '../InputSearchMap'
import { NotificationManager } from "Components/Notifications";

/*********************************************************
 * Enums, Methods, Objects
*********************************************************/
import { TypeObjectMap } from 'Components/Map/providers/enums'
import { Pill } from 'Components';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';
import { useDataGridThrottler } from 'Core/hooks';
/*********************************************************
 * Redux
*********************************************************/
import {
  selectCreateClickMapType,
  enableMorePointers,
  searchLandmarks,
  addPointers,
  setLatLng
} from 'Redux/actions';

import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getVehicleTags = (state) => state.realtimeMapUtilitiesVehiclesListRedux.tagsVehicleList;

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
  [getUnitsCatalog, getReadingsCatalog, getVehicleTags],
  (unitsList, readings, vehicleTags) => {
    let result = []
    unitsList?.forEach((unit) => {
      try {
        if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
        const idUnit = unit.id

        let lastReading = readings?.[idUnit]
        const lat = lastReading?.latitude || unit?.latitude || unit?.lat
        const lng = lastReading?.longitude || unit?.longitude || unit?.lng
        const label = unit?.label || lastReading?.deviceLabel;
        if (!Array.isArray(unit?.tags) || unit.tags.length === 0) {
          unit = { ...unit, tags: [{ color: "000000", hasChildren: false, id: -1, label: "Untagged", parentId: null, path: "" }] }
        }

        //filter unit for existing tag
        unit = {
          ...unit,
          tags: !vehicleTags?.length ? unit.tags : unit.tags?.filter(tag => vehicleTags?.some(vehicleTag => tag?.id == -1 || vehicleTag?.id === tag?.id))
        }

        // if the unit no have tags or none is the group "Untagged", it is omitted
        if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

        if (!lastReading?.haveLatLng) return; // No is entered on the list of vehicle because no have lat and lng

        const item = {
          id: unit.id || lastReading?.deviceId,
          label: label,
          lat: lat ? parseFloat(lat) : 0,
          lng: lng ? parseFloat(lng) : 0,
          latitude: lat ? parseFloat(lat) : 0,
          longitude: lng ? parseFloat(lng) : 0,
          haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
          address: lastReading?.address,
          fulladdress: lastReading?.address?.formatted || '',
          changeLabel: (unit?.label != label),
          changeLatLng: lastReading?.changeLatLng,
          lastReading: lastReading,
          tags: unit.tags || []
        }
        result.push(item);
      } catch (e) {
        console.log('e', e)
        NotificationManager.error('Error', `Error src/components/Map/components/FromToMap/index.js to create show list of vehicles - ${e}`, 3000, null, null, 'error');
      }
    });
    return result
  }
)

/*********************************************************
 * Control to point item into the map
*********************************************************/
const FromToMap = forwardRef((props, ref) => {

  const {
    pills,
    spacing,
    getCoordinates,
    id,
    addMorePoints,
    showAll,
    restart,
    disabled,
    createElements,
    required,
    entities,
    hideClear,
    isPolygon,
    onSelectType,
    onClearMarker,
    maxPoints,
    onChange,
    automaticCleaner = true
  } = props;

  const { messages } = useIntl();

  const inputSearchMapRef = useRef();

  const selectTypes = [
    { name: messages['realtimemaps_address'], id: "ADDRESS", showDefault: true, color: '#66BB6A' },
    { name: messages['realtimemaps_useMapClick'], id: "MAP_CLICK", showDefault: true, color: '#66BB6A' },
    { name: messages['realtimemaps_unit'], id: "UNITS", showDefault: true, color: '#66BB6A' },
    { name: messages['realtimemaps_landmark'], id: "LANDMARK", showDefault: false, color: '#66BB6A' },
    { name: messages['realtimemaps_latLng'], id: "LATLNG", showDefault: false, color: '#66BB6A' },
  ];

  useImperativeHandle(ref, () => ({
    clearInputSearchMap: inputSearchMapRef?.current?.clear,
    setSelectType: setSelectType,
    setSelectUnit: setSelectUnit,
    resetComponent: resetComponent
  }));

  const dispatch = useDispatch()
  const [listTypes, setListTypes] = useState([])
  const [selectType, setSelectType] = useState({ name: messages['realtimemaps_address'], id: "ADDRESS" })
  const [selectUnit, setSelectUnit] = useState([])
  const [selectLandmark, setSelectLandmark] = useState([]);
  const [latitude, setLatitude] = useState();
  const [longitude, setLongitude] = useState();

  const [latitudeErrorMsg, setLatitudeErrorMsg] = useState();
  const [longitudeErrorMsg, setLongitudeErrorMsg] = useState();

  const latRegex = /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,7})?))$/;
  const lngRegex = /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,7})?))$/;

  /*********************************************************
   * Get the redux properties
  *********************************************************/
  const clickLatLng = useSelector((state) => state.realtimeMapRedux.clickLatLng);
  const landmarks = useSelector((state) => state.entitySelectorRedux.landmarks);
  const pointers = useSelector((state) => state.realtimeMapRedux.pointers);

  /*********************************************************
   * use compute data to sync values showing in table
  *********************************************************/
  const computedData = useSelector(state => getData(state) || []);
  const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "changeLatLng", "haveLatLng", "tags"], []);

  // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
  const units = useDataGridThrottler(
    computedData,
    1000,
    () => { },
    "id",
    propertiesToValidate
  );

  useEffect(() => {

    return () => {
      if (automaticCleaner) {
        resetComponent()
      }
    }
  }, []);

  /*********************************************************
   * Load the default elements into the list
  *********************************************************/
  useEffect(() => {
    if (entities?.length) {
      let areEquals = true;
      let newListTypes = listTypes?.map(lt => lt?.id);

      entities?.forEach(e => {
        if (!newListTypes?.includes(e)) {
          areEquals = false;
        }
      });

      if (!areEquals || entities?.length != newListTypes?.length) {
        if (entities?.includes("LANDMARK")) {
          dispatch(searchLandmarks(0, 0, '', true));
        }

        setListTypes(selectTypes.filter(item => entities?.includes(item?.id)));
        setSelectType({ name: messages['realtimemaps_address'], id: "ADDRESS" });
      }
    }
  }, [entities]);

  /*********************************************************
   * Restart the control
  *********************************************************/
  useEffect(() => {
    if (restart > 0) {
      dispatch(selectCreateClickMapType(TypeObjectMap.Default));
      dispatch(setLatLng(null));
      setListTypes();
      setSelectType({ name: messages['realtimemaps_address'], id: "ADDRESS" });
      setSelectUnit([]);
      setSelectLandmark([]);
      setLatitude();
      setLongitude();
      setLatitudeErrorMsg();
      setLongitudeErrorMsg();

      if (props.onSelected) {
        props.onSelected(null)
      }
    }
  }, [restart])

  /*********************************************************
   * Enable add multiple points on map
  *********************************************************/
  useEffect(() => {
    if (addMorePoints) {
      dispatch(enableMorePointers(true))
    }
  }, [addMorePoints])

  /*********************************************************
   * Get the las position on the even on map
  *********************************************************/
  useEffect(() => {
    if (clickLatLng && props.onGetPosition) {
      props.onGetPosition(clickLatLng, id)
    }
  }, [clickLatLng])

  /*********************************************************
   * Create point on map
  *********************************************************/
  useEffect(() => {
    if (onSelectType) onSelectType(selectType)
    if (!getCoordinates && selectType.id == "MAP_CLICK" && !props.noMapClick) {
      dispatch(selectCreateClickMapType(TypeObjectMap.CreatePointer))
    } else if (getCoordinates && ["MAP_CLICK"].includes(selectType.id)) {
      dispatch(selectCreateClickMapType(TypeObjectMap.MapClick));
    } else if (!isPolygon) {
      dispatch(selectCreateClickMapType(TypeObjectMap.Default));
    } else if (isPolygon) {
      dispatch(selectCreateClickMapType(TypeObjectMap.Polygon, maxPoints || 0));
    }
  }, [selectType]);


  useEffect(() => {
    setLatitudeErrorMsg();
    setLongitudeErrorMsg();

    if (latitude && longitude) {
      if (latRegex?.test(latitude) && lngRegex?.test(longitude)) {
        if (props.onGetPosition) props.onGetPosition({ lat: parseFloat(latitude), lng: parseFloat(longitude) }, id);
      } else if (!latRegex?.test(latitude)) {
        onClearMarker && onClearMarker();
        setLatitudeErrorMsg(messages['realtimemaps_vehicledirections_latError']);
      } else if (!lngRegex?.test(longitude)) {
        onClearMarker && onClearMarker();
        setLongitudeErrorMsg(messages['realtimemaps_vehicledirections_lngError']);
      }
    } else if (latitude && !latRegex?.test(latitude)) {
      onClearMarker && onClearMarker();
      setLatitudeErrorMsg(messages['realtimemaps_vehicledirections_latError']);
    } else if (longitude && !lngRegex?.test(longitude)) {
      onClearMarker && onClearMarker();
      setLongitudeErrorMsg(messages['realtimemaps_vehicledirections_lngError']);
    }
  }, [latitude, longitude]);

  /*********************************************************
   * New select element of the list
  *********************************************************/
  const onSelectedElement = (event) => {
    if (!disabled) {
      setSelectType(event);
      onChange && onChange(event)
      setLatitude();
      setLongitude();

      onClearMarker && onClearMarker();

      if (props.onSelected) {
        if (["MAP_CLICK", "LATLNG"]?.includes(event.id)) {
          props.onSelected(id)
        } else {
          props.onSelected(null)
        }
      }
      props?.onChangeSelectItem && props.onChangeSelectItem(event)
    }
  }

  /*********************************************************
   * show the marker into the map
  *********************************************************/
  const onSelectLandmark = (item) => {
    const pointsArray = [...pointers]

    let itemPoint = pointsArray.find(item => item.id == id)
    if (itemPoint) {
      itemPoint.lat = item.latitude
      itemPoint.lng = item.longitude

      if (props.onSelectLandmark) {
        setSelectLandmark(item)
        props.onSelectLandmark(itemPoint, id)
        return
      }

    } else {
      const pointer = {
        id: item?.id,
        lat: item.latitude,
        lng: item.longitude,
        label: 'Map Point',
        eventName: 'no event',
        address: item.address
      }
      pointsArray.push(pointer)
      if (props.onSelectLandmark) {
        setSelectLandmark(item)
        props.onSelectLandmark(pointer, id)
        return
      }
    }

    //Clear the markers
    dispatch(addPointers([]));
    //add new elements in map
    window.clearPointers = setTimeout(() => {
      dispatch(addPointers([...pointsArray]));
      window.clearPointers = null;
    }, 300);


    setSelectLandmark(item)
  }

  /*********************************************************
   * Select the unit to the item list
  *********************************************************/
  const onSelectSelectUnit = (item) => {
    setSelectUnit(item)
    if (props.onSelectSelectUnit) {
      props.onSelectSelectUnit(item)
    }
  }

  const resetComponent = () => {
    dispatch(selectCreateClickMapType(TypeObjectMap.Default));
    dispatch(setLatLng(null));
    setListTypes();
    setSelectType({ name: messages['realtimemaps_address'], id: "ADDRESS" });
    setSelectUnit();
    setSelectLandmark();
    setLatitude();
    setLongitude();
    setLatitudeErrorMsg();
    setLongitudeErrorMsg();
  }

  return (
    <Grid container direction="row" justifyContent="space-between" alignItems="center">
      {
        pills ?
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: 10, marginBottom: 0, width: '100%' }}
          >
            {
              listTypes?.map((item, key) => {
                const { id } = item;
                let selected = selectType?.id === id ? false : true;
                let styles = { width: '100%' };
                if (selected) {
                  styles = {
                    ...styles,
                    ...{
                      backgroundColor: "#F1F1F1",
                      color: "#484848",
                      border: "1px solid #F1F1F1"
                    }
                  };
                }

                return (
                  <Grid item={true} xs key={key} style={{ marginRight: 3, marginBottom: 4 }}>
                    <Pill
                      style={styles}
                      item={item}
                      outlined={selected}
                      onClick={(value) => onSelectedElement(value)}
                    />
                  </Grid>
                )
              })
            }
          </Grid> :
          <Grid item xs={12} id={"selectOptionsAddress"}>
            <Select
              id="landmarkGroup "
              value={selectType}
              onChange={(event) => onSelectedElement(event)}
              options={listTypes}
              textField="name"
              valueField="id"
              placeholder={messages['realtimemaps_landmarks_fromto']}
              disabled={disabled}
            />
          </Grid>
      }


      {
        selectType.id == "ADDRESS"
        &&
        <Grid item xs={12}>
          <div style={{ paddingTop: 8 }}>
            <div style={{ paddingBottom: 10 }}>
              <label className="input-normal-label">
                {messages['address']}
                {
                  (required || required === undefined) && <span className="text-danger"> *</span>
                }
              </label>
            </div>
            <InputSearchMap
              ref={inputSearchMapRef}
              disabled={disabled}
              normalView
              noMap
              onGetAddress={props.onGetAddress}
              hideClear={hideClear}
              onClear={props.onClear}
              createElements={createElements}
              draggable
              restart={restart}
              spacing={spacing ? spacing : 2}
              messages={messages}
              onKeyPress={props.onKeyPress}
              placeholder={messages['search']}
              noOptionsText={messages['noOptions']}
            />
          </div>
        </Grid>
      }

      {
        selectType.id == "UNITS"
        &&
        <Grid item xs={12}>
          <Select
            id="units"
            value={selectUnit}
            onChange={(event) => onSelectSelectUnit(event)}
            options={units}
            textField="label"
            valueField="id"
            placeholder={messages['realtimemaps_vehicledirections_units']}
            label={messages['unit']}
            required={(required || required === undefined) ? true : false}
            disabled={disabled}
          />
        </Grid>
      }

      {
        selectType.id == "LANDMARK"
        &&
        <Grid item xs={12}>
          <Select
            id="landmark"
            value={selectLandmark}
            onChange={(event) => onSelectLandmark(event)}
            options={landmarks}
            textField="name"
            valueField="id"
            label={messages['landmark']}
            placeholder={messages['realtimemaps_vehicledirections_landmark']}
            disabled={disabled}
            required
          />
        </Grid>
      }

      {
        selectType.id == "LATLNG"
        &&
        <Grid item xs={12}>
          <div>
            <Input
              id="latitude"
              type="number"
              label={messages['realtimemaps_latitude']}
              value={latitude}
              onChange={(newValue) => {
                setLatitude(newValue);
              }}
              regex={latRegex}
              decimalPlaces={7}
              required
            />

            {
              latitudeErrorMsg &&
              <small className="text-danger" style={{ paddingTop: 5 }}>
                {latitudeErrorMsg}
              </small>
            }
          </div>

          <div>
            <Input
              id="longitude"
              type="number"
              label={messages['realtimemaps_longitude']}
              value={longitude}
              onChange={(newValue) => {
                setLongitude(newValue);
              }}
              regex={lngRegex}
              decimalPlaces={7}
              required
            />

            {
              longitudeErrorMsg &&
              <small className="text-danger" style={{ paddingTop: 5 }}>
                {longitudeErrorMsg}
              </small>
            }
          </div>
        </Grid>
      }
    </Grid>
  )

});

//export default injectIntl(FromToMap, { withRef: true })
export default FromToMap;
